import {
  BadgeCheck,
  ChevronDown,
  CircleAlert,
  CircleX,
  ListChecks,
  MinusCircle,
  Quote,
  Scale,
  ShieldAlert,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";

import type {
  AssessmentMetadata,
  EligibilityDebate as EligibilityDebatePayload,
} from "../../api/use-claim-insights";
import { EligibilityDebate } from "./eligibility-debate";

/**
 * "Compréhension du dossier" — a concise, per-claim-type reading of the ASSESSMENT insight, replacing
 * the old generic KPI grid (TECH-705). Grounded in the field-presence of the last 500 insights:
 * competence + expert verdict are present ~100% of the time (so they lead / anchor the panel);
 * préavis + réacheminement are present 100% for CANCELLED but only ~41% for DELAYED, so they only
 * surface for a cancellation or when actually populated. Compétence juridique sits on top.
 */

const NOTICE_BRACKET_LABELS: Record<string, string> = {
  PREAVIS_MOINS_7J: "Moins de 7 jours",
  PREAVIS_7_13J: "7 à 13 jours",
  PREAVIS_14J_PLUS: "14 jours ou plus",
};

const label = (map: Record<string, string>, value?: string | null) => (value ? (map[value] ?? value) : null);

/** Format a pipeline ISO timestamp as a French date, or null when absent/unparseable. */
function formatDate(iso?: string | null): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString("fr-FR");
}

const CONTROL_CHARS = /\p{Cc}/gu;
const cleanLine = (line?: string | null) =>
  (line || "").replace(CONTROL_CHARS, " ").replace(/\s+/g, " ").trim();
// The pipeline's prose carries inline citation refs ("(sources[0])", "(source 1)", "[sources 1, 2]");
// the backing PNR line is surfaced separately, so strip the refs from the displayed reasoning.
const SOURCE_REFS = /\s*[([]?\s*sources?\s*\[?\s*\d+(?:\s*,\s*\d+)*\s*\]?\s*[)\]]?/gi;
/** Clean + de-ref a reasoning string, shown in full (no truncation); undefined when it's empty. */
function cleanReason(text?: string | null): string | undefined {
  const value = cleanLine(text).replace(SOURCE_REFS, "").replace(/\s+/g, " ").trim();
  return value || undefined;
}

/** PNR source lines of a block, surfaced under the criterion. Prefers the authentic GDS lines
 * (type "pnr") and shows all of them (deduped); falls back to "pnr_refined" when no raw line exists.
 * `includeRefined` shows both types together (raw first) instead of the fallback behaviour. */
function pnrSources(
  block?: { sources?: Array<{ type?: string; line?: string }> } | null,
  { includeRefined = false }: { includeRefined?: boolean } = {},
): string[] {
  const sources = block?.sources ?? [];
  const raw = sources.filter((entry) => entry.type === "pnr");
  const refined = sources.filter((entry) => entry.type === "pnr_refined");
  const chosen = includeRefined ? [...raw, ...refined] : raw.length > 0 ? raw : refined;
  return [...new Set(chosen.map((entry) => cleanLine(entry.line)).filter(Boolean))];
}

// Status drives a single dot — the whole panel reads at a glance without colour noise.
type Status = "ok" | "warn" | "bad" | "muted";
const DOT: Record<Status, string> = {
  ok: "bg-emerald-500",
  warn: "bg-amber-500",
  bad: "bg-rose-500",
  muted: "bg-slate-300",
};

// Value text echoes the verdict pill: an invalid criterion (e.g. "Origine volontaire suspectée")
// reads red, a doubt reads amber; the normal states stay neutral.
const VALUE_TEXT: Record<Status, string> = {
  ok: "text-slate-900",
  warn: "text-amber-700",
  bad: "text-rose-700",
  muted: "text-slate-900",
};

// A colored status icon per criterion (✓ / ✕ / ! / —) reads faster than a bare dot.
const STATUS_ICON: Record<Status, typeof BadgeCheck> = {
  ok: BadgeCheck,
  warn: CircleAlert,
  bad: CircleX,
  muted: MinusCircle,
};
const ICON_COLOR: Record<Status, string> = {
  ok: "text-sky-500",
  warn: "text-amber-500",
  bad: "text-rose-500",
  muted: "text-slate-300",
};

interface Criterion {
  key: string;
  label: string;
  value: string;
  status: Status;
  /** Secondary line (trajet, reasoning) shown under the value. */
  detail?: string;
  /** Raw PNR source lines, shown as a monospace citation under the criterion. */
  sources?: string[];
}

/** The decisive criteria for the claim's typology — only the ones that legally matter, in order. */
function decisiveCriteria(raw: AssessmentMetadata): Criterion[] {
  const cause = raw.perturbation?.cause;
  const rerouting = raw.rerouting;
  const criteria: Criterion[] = [];

  // Compétence juridique leads: in/out-of-scope + the pipeline's French trajet summary.
  const trajet = raw.context?.selectedTrajet;
  const inScope = !raw.competence?.notCompetence;
  if (raw.competence || trajet) {
    criteria.push({
      key: "competence",
      label: "Compétence Juridique",
      value: inScope ? "Dossier dans le champ d'application" : "Hors champ d'application",
      status: inScope ? "ok" : "bad",
      detail:
        trajet?.description ?? (trajet ? `${trajet.departureAirport} → ${trajet.arrivalAirport}` : undefined),
    });
  }

  // Caractère involontaire — developed: the reasoning + the backing PNR source line.
  if (raw.involuntary?.isInvoluntary !== undefined) {
    const involuntary = raw.involuntary.isInvoluntary;
    criteria.push({
      key: "involuntary",
      label: "Caractère involontaire",
      value: involuntary ? "Involontaire" : "Origine volontaire suspectée",
      status: involuntary ? (raw.involuntary.isDoubt ? "warn" : "ok") : "bad",
      detail: cleanReason(raw.involuntary.reasoning),
      sources: pnrSources(raw.involuntary),
    });
  }

  // Réacheminement + préavis are art. 5 cancellation constructs: shown for an annulation, and for the
  // other typologies only when the pipeline assessed them. `anyValidRerouting` has three states, named
  // explicitly and never as "non applicable". "Non valable" ≠ "aucun réacheminement" — the passenger
  // may well have been rerouted (visible in Itinéraire Final) onto a flight that misses the EU261
  // thresholds (e.g. arrives too late); the reasoning detail distinguishes that from "no offer at all".
  const anyValidRerouting = rerouting?.anyValidRerouting;
  if (cause === "CANCELLED" || anyValidRerouting !== undefined) {
    criteria.push({
      key: "rerouting",
      label: "Réacheminement",
      value:
        anyValidRerouting === true
          ? "Réacheminement valide proposé"
          : anyValidRerouting === false
            ? "Réacheminement non valable"
            : "À vérifier",
      // A valid rerouting offered defeats the claim → negative criterion (red ✕), not neutral.
      status: anyValidRerouting === false ? "ok" : anyValidRerouting === true ? "bad" : "warn",
      detail: cleanReason(rerouting?.reasoning),
      sources: pnrSources(rerouting, { includeRefined: true }),
    });
  }

  // Préavis rides with Réacheminement (same art. 5 pair — shown whenever the other is).
  const noticeDays = rerouting?.noticeDays;
  const noticePresent = noticeDays != null || !!rerouting?.noticeBracket;
  if (cause === "CANCELLED" || anyValidRerouting !== undefined || noticePresent) {
    const notifiedOn = formatDate(raw.perturbation?.notificationCommitDate);
    const noticeValue =
      noticeDays != null
        ? `${noticeDays} jour(s) avant départ`
        : (label(NOTICE_BRACKET_LABELS, rerouting?.noticeBracket) ?? "—");
    // Informed ≥14 days before departure → no compensation (art. 5), the criterion fails.
    const longNotice = rerouting?.noticeBracket === "PREAVIS_14J_PLUS" || (noticeDays ?? 0) >= 14;
    criteria.push({
      key: "notice",
      label: "Préavis",
      // The notification date rides along inline so the relative "X jour(s)" reads as an absolute fact.
      // Sources: the notice is computed by the rerouting block, so its PNR lines are the provenance
      // (notification commit + original schedule) — shown here too, even if Réacheminement cites them.
      value: notifiedOn ? `${noticeValue} (notifié le ${notifiedOn})` : noticeValue,
      status: !noticePresent ? "muted" : longNotice ? "bad" : "ok",
      sources: pnrSources(rerouting),
    });
  }

  return criteria;
}

const VERDICT = {
  eligible: { label: "Éligible", tone: "bg-emerald-50 text-emerald-700 ring-emerald-200", dot: "ok" },
  notEligible: { label: "Non éligible", tone: "bg-rose-50 text-rose-700 ring-rose-200", dot: "bad" },
  unsure: { label: "À confirmer", tone: "bg-amber-50 text-amber-700 ring-amber-200", dot: "warn" },
  unknown: { label: "À évaluer", tone: "bg-slate-100 text-slate-600 ring-slate-200", dot: "muted" },
} satisfies Record<string, { label: string; tone: string; dot: Status }>;

/**
 * Verdict shown in the header pill + its reasoning. Prefers the debate's reconciled
 * `mostProbableVerdict`; falls back to the expert block. "À confirmer" when the two disagree, or when
 * no debate resolved it and the expert itself flagged a doubt (`expert.isDoubt`). The reasoning falls
 * back to the expert's doubtReason / reasoning when there's no debate verdict text (common: the
 * CONTRADICTIONS insight is often an empty `{ error: "" }`).
 */
function verdictOf(raw: AssessmentMetadata, debate?: EligibilityDebatePayload | null) {
  const expert = raw.expert?.isEligible;
  const expertDoubt = raw.expert?.isDoubt === true;
  const debated = debate?.mostProbableVerdict?.toUpperCase();
  const debatedEligible = debated === "ELIGIBLE" ? true : debated === "NOT_ELIGIBLE" ? false : undefined;
  const reason =
    cleanReason(debate?.verdictReason) ??
    cleanReason(raw.expert?.doubtReason) ??
    cleanReason(raw.expert?.reasoning);

  const disagree = debatedEligible !== undefined && expert !== undefined && debatedEligible !== expert;
  const eligible = debatedEligible ?? expert;

  const kind =
    disagree || (debatedEligible === undefined && expertDoubt)
      ? "unsure"
      : eligible === undefined
        ? "unknown"
        : eligible
          ? "eligible"
          : "notEligible";
  return { ...VERDICT[kind], reason };
}

/** One criterion row: label + colored value with a status icon, reasoning detail, and PNR citation(s). */
function CriterionRow({ criterion }: { criterion: Criterion }) {
  const StatusIcon = STATUS_ICON[criterion.status];
  return (
    <div className="py-2.5">
      <div className="flex items-start justify-between gap-3">
        <dt className="text-base font-medium text-slate-800">{criterion.label}</dt>
        <dd
          className={cn(
            "inline-flex items-center gap-1.5 text-right text-base",
            VALUE_TEXT[criterion.status],
            criterion.status === "bad" ? "font-semibold" : "font-medium",
          )}
        >
          {criterion.value}
          <StatusIcon className={cn("h-5 w-5 flex-none", ICON_COLOR[criterion.status])} />
        </dd>
      </div>
      {criterion.detail ? (
        <p className="mt-1 text-sm leading-relaxed text-slate-500">{criterion.detail}</p>
      ) : null}
      {criterion.sources && criterion.sources.length > 0 ? (
        <figure className="mt-2 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <figcaption className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-2.5 py-1">
            <Quote className="h-3 w-3 fill-slate-300 text-slate-300" />
            <span className="font-mono text-xs font-medium tracking-wider text-slate-400 uppercase">
              Source PNR
            </span>
          </figcaption>
          <div className="divide-y divide-slate-100">
            {criterion.sources.map((line) => (
              <blockquote
                key={line}
                className="overflow-x-auto px-2.5 py-2 font-mono text-sm leading-relaxed whitespace-pre text-slate-700 select-all"
              >
                {line}
              </blockquote>
            ))}
          </div>
        </figure>
      ) : null}
    </div>
  );
}

/**
 * Compact frame of only the negative (invalid) criteria — the reasons a claim is non éligible. Used
 * inline in the "Non éligible" card, where the full "Compréhension du dossier" would be redundant.
 */
export function NegativeCriteria({ assessment }: { assessment: AssessmentMetadata | null }) {
  const negatives = decisiveCriteria(assessment ?? {}).filter((criterion) => criterion.status === "bad");
  if (negatives.length === 0) {
    return null;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-rose-200 bg-white p-4">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-rose-600">
        <TriangleAlert className="h-3.5 w-3.5" /> Critères en défaut
      </p>
      <dl className="divide-y divide-slate-100">
        {negatives.map((criterion) => (
          <CriterionRow key={criterion.key} criterion={criterion} />
        ))}
      </dl>
    </div>
  );
}

/** Whether the assessment yields any invalid (negative) criterion — the "critères en défaut" case. */
export function hasNegativeCriteria(assessment: AssessmentMetadata | null): boolean {
  return decisiveCriteria(assessment ?? {}).some((criterion) => criterion.status === "bad");
}

interface AiInsightsPanelProps {
  assessment: AssessmentMetadata | null;
  debate?: EligibilityDebatePayload | null;
}

export function AiInsightsPanel({ assessment, debate }: AiInsightsPanelProps) {
  const [debateOpen, setDebateOpen] = useState(false);

  const raw: AssessmentMetadata = assessment ?? {};
  const verdict = verdictOf(raw, debate);
  const criteria = decisiveCriteria(raw);

  // Concise signals — only surfaced when set (double indemnisation, hors règlement, CX).
  const context = raw.context ?? {};
  const remarks = raw.preprocess?.remarks ?? {};
  const signals = [
    context.doubleCompensation ? "Double indemnisation" : null,
    context.notRegulation ? "Hors règlement" : null,
    remarks.hasExtraordinaryCircumstanceHint ? "Circonstance extraordinaire possible" : null,
  ].filter((signal): signal is string => signal !== null);

  const warnings = (debate?.warnings ?? [])
    .map((warning) =>
      typeof warning === "string" ? cleanLine(warning) : cleanLine(warning?.message ?? warning?.line),
    )
    .filter(Boolean);
  const hasDebate = Boolean(debate?.eligible || debate?.notEligible || debate?.verdictReason);

  return (
    <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      {/* Header — same UI as "Confirmer l'incident": a colored icon + concise title */}
      <div className="mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <Scale className="h-5 w-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-slate-800">Compréhension du dossier</h3>
          </div>
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset",
              verdict.tone,
            )}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full", DOT[verdict.dot])} />
            {verdict.label}
          </span>
        </div>
        {verdict.reason ? (
          <p className="mt-2.5 text-pretty text-base leading-relaxed text-slate-600">{verdict.reason}</p>
        ) : null}
      </div>

      {/* Critères décisifs — only the ones that matter for this typology (Compétence first) */}
      {criteria.length > 0 ? (
        <div className="mt-4">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-indigo-600">
            <ListChecks className="h-3.5 w-3.5" />
            Critères décisifs
          </p>
          <dl className="divide-y divide-slate-100">
            {criteria.map((criterion) => (
              <CriterionRow key={criterion.key} criterion={criterion} />
            ))}
          </dl>
        </div>
      ) : null}

      {/* Signals + points de vigilance + collapsible debate */}
      {signals.length > 0 || warnings.length > 0 || hasDebate ? (
        <div className="mt-5 space-y-4 border-t border-slate-100 pt-4">
          {signals.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {signals.map((signal) => (
                <span
                  key={signal}
                  className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200"
                >
                  <ShieldAlert className="h-3 w-3" />
                  {signal}
                </span>
              ))}
            </div>
          ) : null}

          {warnings.length > 0 ? (
            <div>
              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-500">
                <TriangleAlert className="h-3 w-3" /> Points de vigilance ({warnings.length})
              </p>
              <ul className="space-y-0.5">
                {warnings.map((warning) => (
                  <li key={warning} className="text-xs leading-relaxed text-slate-600">
                    • {warning}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {hasDebate ? (
            <div>
              <button
                type="button"
                onClick={() => setDebateOpen((current) => !current)}
                className="flex w-full items-center justify-between text-left text-xs font-semibold text-slate-600 hover:text-slate-900"
              >
                <span>Analyse contradictoire de l'éligibilité</span>
                <ChevronDown className={cn("h-4 w-4 transition-transform", debateOpen && "rotate-180")} />
              </button>
              {debateOpen && debate ? (
                <div className="mt-3">
                  <EligibilityDebate debate={debate} />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
